import React from 'react';
import { motion } from 'framer-motion';
import { Snowflake, Mountain, ThermometerSnowflake, Shield } from 'lucide-react';

const ColdWeatherBanner = () => {
  return (
    <section
      data-testid="cold-weather-banner"
      className="relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, #0A1628 0%, #0F2240 40%, #1A2744 100%)',
        padding: '60px 0',
      }}
    >
      {/* Subtle frost/mountain silhouette bg */}
      <div className="absolute inset-0 pointer-events-none" style={{ opacity: 0.04 }}>
        <div className="absolute bottom-0 left-0 right-0" style={{
          height: '60%',
          background: 'linear-gradient(170deg, transparent 30%, rgba(255,255,255,0.08) 50%, transparent 70%)',
        }} />
      </div>

      {/* Large background temperature */}
      <div className="absolute right-6 top-1/2 -translate-y-1/2 font-display pointer-events-none hidden lg:block" style={{
        fontSize: '200px', fontWeight: 700, color: 'rgba(100,180,255,0.04)', lineHeight: 1,
      }}>
        -40°
      </div>

      <div className="container-main relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr] gap-10 items-center">
          {/* Content */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 mb-5 px-4 py-2" style={{
                border: '1px solid rgba(100,180,255,0.2)',
                borderRadius: '4px',
                background: 'rgba(100,180,255,0.05)',
              }}>
                <Snowflake size={12} style={{ color: '#64B4FF' }} />
                <span className="font-mono" style={{
                  fontSize: '0.6rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: '#64B4FF',
                }}>
                  Core Specialization
                </span>
              </div>

              <h2 className="font-display text-white mb-4" style={{
                fontSize: 'clamp(1.6rem, 4vw, 2.6rem)', fontWeight: 700, lineHeight: 1.15,
              }}>
                Engineered for{' '}
                <span style={{ color: '#64B4FF' }}>Extreme Cold.</span>
              </h2>

              <p className="font-body mb-8" style={{
                fontSize: '1.05rem', fontWeight: 300, color: 'rgba(255,255,255,0.65)', lineHeight: 1.8, maxWidth: '520px',
              }}>
                We specialize in designing and manufacturing complete cold weather clothing systems rated for{' '}
                <span className="font-display" style={{ color: '#FFFFFF', fontWeight: 600 }}>-40°C</span>{' '}
                extreme conditions. High-altitude combat gear, polar expedition layers, and extreme-environment workwear — built with precision insulation engineering.
              </p>

              <div className="flex flex-wrap gap-2">
                {['High-Altitude Systems', 'Multi-Layer Insulation', 'Down & Synthetic Fill', 'Seam-Sealed Construction'].map((tag, i) => (
                  <span key={i} className="font-mono" style={{
                    fontSize: '0.55rem', letterSpacing: '0.08em', textTransform: 'uppercase',
                    padding: '6px 14px', borderRadius: '4px',
                    background: 'rgba(100,180,255,0.1)', color: '#64B4FF',
                    border: '1px solid rgba(100,180,255,0.2)',
                  }}>
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Temperature Infographic */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(100,180,255,0.12)',
              borderRadius: '14px',
              padding: '2rem',
            }}>
              {/* Temperature Scale Visual */}
              <div className="flex items-center gap-3 mb-6">
                <ThermometerSnowflake size={20} style={{ color: '#64B4FF' }} />
                <span className="font-mono" style={{
                  fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.15em', textTransform: 'uppercase',
                }}>
                  Rated Temperature
                </span>
              </div>

              {/* Temperature display */}
              <div className="flex items-center justify-center mb-6">
                <span className="font-display" style={{ fontSize: '3.5rem', fontWeight: 700, color: '#64B4FF', lineHeight: 1 }}>-40°C</span>
              </div>

              {/* Capability cards */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { icon: Mountain, label: 'High Altitude', val: '5,000m+' },
                  { icon: Shield, label: 'Military Grade', val: 'Combat Rated' },
                  { icon: Snowflake, label: 'Insulation', val: 'Multi-Layer' },
                  { icon: ThermometerSnowflake, label: 'Tested To', val: '-40°C' },
                ].map((item, i) => (
                  <div key={i} style={{
                    background: 'rgba(100,180,255,0.05)',
                    borderRadius: '8px',
                    padding: '12px',
                    border: '1px solid rgba(100,180,255,0.08)',
                  }}>
                    <item.icon size={14} style={{ color: '#64B4FF', marginBottom: '8px' }} />
                    <span className="font-display block text-white" style={{ fontSize: '1rem', fontWeight: 700 }}>{item.val}</span>
                    <span className="font-mono" style={{ fontSize: '0.5rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{item.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ColdWeatherBanner;
